<template>
  <div class="pd-embed-control" :class="{ 'pd-embed-contorl-disabled': !editable }">
    <div
      class="flex-wrap"
      style="padding-right:32px;padding-left:5px;line-height:30px;cursor:pointer"
      :style="{ color: value && value.id ? '#39f' : '#aaa' }"
      @click="openModal"
    >
      {{ displayLabel }}
    </div>

    <Modal v-model="modalSelector" :width="600" title="数据源" footer-hide @on-cancel="handleCancel">
      <div class="ds-modal">
        <!-- 上方：当前接口与参数 -->
        <div class="ds-section">
          <div class="ds-section-title">接口参数</div>
          <div v-if="!draftId" class="ds-empty">请先在下方列表中选择数据接口</div>
          <template v-else>
            <div class="ds-current">
              <span class="ds-current-name">{{ draftSource.name || '未命名' }}</span>
              <span class="ds-current-meta">{{ draftSource.type }} · {{ draftSource.method }} · {{ draftSource.url }}</span>
            </div>
            <Spin v-if="loadingProperties" fix />
            <div v-else-if="!properties.length" class="ds-empty subtle">该接口暂无配置参数</div>
            <table v-else class="ds-params-table">
              <thead>
                <tr>
                  <th class="col-name">参数</th>
                  <th class="col-val">值</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in properties" :key="p.key">
                  <td class="col-name">
                    <span>{{ propLabel(p) }}</span>
                    <span v-if="p.required" class="ds-req">*</span>
                    <Tooltip v-if="p.description" :content="p.description" placement="top" max-width="320" transfer>
                      <Icon type="ios-help-circle-outline" class="ds-help" />
                    </Tooltip>
                  </td>
                  <td class="col-val">
                    <Input
                      v-model="draftOption[p.key]"
                      size="small"
                      :placeholder="p.placeholder || defaultPlaceholder(p)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>

        <div class="ds-row-default">
          <span class="ds-switch-label">作为默认值</span>
          <i-switch v-model="draftAsDefault" :disabled="!draftId" size="small" />
        </div>

        <!-- 下方：接口列表 -->
        <div class="ds-section">
          <div class="ds-section-title">选择接口</div>
          <div class="ds-list">
            <div
              v-for="ds in dataSources"
              :key="ds.id"
              class="col-item"
              :class="{ 'col-item-selected': draftId === ds.id }"
              @click="selectSource(ds)"
            >
              <div class="ds-li-main">
                <span class="ds-li-name">{{ ds.name || '未命名' }}</span>
                <span class="ds-li-badge">{{ ds.type }}</span>
              </div>
              <div class="ds-li-sub">{{ ds.method }} {{ ds.url }}</div>
            </div>
            <div v-if="!dataSources.length" class="ds-empty subtle">暂无数据接口，请确认已加载 oa/data_sources</div>
          </div>
        </div>

        <div class="flex-wrap flex-right ds-footer">
          <Button size="small" @click="handleClear">清除</Button>
          <Button size="small" style="margin-left:8px" @click="handleCancel">取消</Button>
          <Button type="primary" size="small" style="margin-left:8px" @click="handleSave">确定</Button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';

export default {
  props: {
    value: {
      type: Object,
      default: null,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    option: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      modalSelector: false,
      draftId: null,
      draftOption: {},
      draftAsDefault: false,
      properties: [],
      loadingProperties: false,
    };
  },
  computed: {
    dataSources() {
      return this.$store.getters['forms/data_sources'] || [];
    },
    draftSource() {
      if (!this.draftId) return {};
      return this.dataSources.find((v) => v.id === this.draftId) || {};
    },
    displayLabel() {
      const v = this.value;
      if (!v || !v.id) return '请选择数据源';
      const name = v.name || (this.dataSources.find((d) => d.id === v.id) || {}).name;
      return name || `接口 #${v.id}`;
    },
  },
  watch: {
    modalSelector(open) {
      if (open) {
        this.syncDraftFromValue();
        if (this.draftId) this.getProperties();
        else {
          this.properties = [];
        }
      }
    },
  },
  mounted() {
    this.$api.afterLogin(() => {
      this.getData();
    });
  },
  methods: {
    openModal() {
      if (!this.editable) return;
      if (!this.dataSources.length) this.getData();
      this.modalSelector = true;
    },
    syncDraftFromValue() {
      const v = this.value;
      if (v && typeof v === 'object' && v.id != null) {
        this.draftId = v.id;
        this.draftOption = cloneDeep(v.option || {});
        this.draftAsDefault = !!v.as_default;
      } else {
        this.draftId = null;
        this.draftOption = {};
        this.draftAsDefault = false;
      }
    },
    selectSource(ds) {
      if (this.draftId === ds.id) return;
      this.draftId = ds.id;
      this.draftOption = {};
      this.getProperties();
    },
    propLabel(p) {
      return p.label || p.name || p.key;
    },
    defaultPlaceholder(p) {
      if (p.default !== undefined && p.default !== null && p.default !== '') return String(p.default);
      return '请输入';
    },
    getData() {
      this.$store.dispatch('forms/getDataSources');
    },
    getProperties() {
      if (!this.draftId) {
        this.properties = [];
        return;
      }
      this.loadingProperties = true;
      this.$api
        .get(`oa/data_sources/${this.draftId}/properties`)
        .then((res) => {
          const list = (res.data && res.data.data) || [];
          this.properties = list;
          list.forEach((p) => {
            if (p.key == null) return;
            if (this.draftOption[p.key] === undefined) {
              if (p.default !== undefined) this.$set(this.draftOption, p.key, p.default);
              else this.$set(this.draftOption, p.key, '');
            }
          });
        })
        .finally(() => {
          this.loadingProperties = false;
        });
    },
    handleSave() {
      if (!this.draftId) {
        this.$emit('input', undefined);
        this.modalSelector = false;
        return;
      }
      const src = this.dataSources.find((d) => d.id === this.draftId);
      if (!src) {
        this.$Message.warning('所选接口不在列表中，请重新选择');
        return;
      }
      this.$emit('input', {
        id: this.draftId,
        name: src.name,
        option: cloneDeep(this.draftOption),
        as_default: this.draftAsDefault,
      });
      this.modalSelector = false;
    },
    handleClear() {
      this.$emit('input', undefined);
      this.modalSelector = false;
    },
    handleCancel() {
      this.modalSelector = false;
    },
  },
};
</script>

<style lang="less" scoped>
.ds-modal {
  position: relative;
  min-height: 120px;
}
.ds-section {
  margin-bottom: 14px;
}
.ds-section-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--text1, #333);
  margin-bottom: 8px;
  border-left: 3px solid var(--primary, #2d8cf0);
  padding-left: 8px;
  line-height: 1.2;
}
.ds-empty {
  padding: 12px;
  text-align: center;
  color: var(--subtext2, #999);
  font-size: 12px;
  border: 1px dashed var(--border, #e8eaec);
  border-radius: 4px;
}
.ds-empty.subtle {
  border: none;
  padding: 8px;
}
.ds-current {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
  padding: 8px 10px;
  background: rgba(45, 140, 240, 0.06);
  border-radius: 4px;
  font-size: 12px;
}
.ds-current-name {
  font-weight: 600;
  color: var(--text1, #333);
}
.ds-current-meta {
  color: var(--text2, #666);
  word-break: break-all;
}
.ds-params-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.ds-params-table th,
.ds-params-table td {
  border: 1px solid var(--border, #e8eaec);
  padding: 6px 8px;
  vertical-align: middle;
}
.ds-params-table th {
  background: #f8f8f9;
  text-align: left;
  font-weight: 600;
}
.col-name {
  width: 38%;
}
.col-val {
  width: 62%;
}
.ds-req {
  color: #ed4014;
  margin-left: 2px;
}
.ds-help {
  margin-left: 4px;
  cursor: help;
  color: var(--subtext2, #999);
  vertical-align: middle;
}
.ds-row-default {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  font-size: 12px;
}
.ds-switch-label {
  color: var(--text2, #666);
}
.ds-list {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid var(--border, #e8eaec);
  border-radius: 4px;
}
.ds-li-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.ds-li-name {
  font-weight: 500;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ds-li-badge {
  font-size: 11px;
  padding: 0 6px;
  border-radius: 3px;
  background: #f0f0f0;
  color: var(--text2, #666);
  flex-shrink: 0;
}
.ds-li-sub {
  margin-top: 4px;
  font-size: 11px;
  color: var(--subtext2, #999);
  word-break: break-all;
}
.ds-footer {
  padding-top: 12px;
  border-top: 1px solid var(--border, #e8eaec);
  margin-top: 4px;
}

.col-item {
  height: auto;
  min-height: 26px;
  padding: 8px 10px;
  cursor: pointer;
  border-bottom: 1px solid var(--border, #e8eaec);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
}
.col-item:last-child {
  border-bottom: none;
}
.col-item-selected {
  background: var(--primary, #2d8cf0);
  color: var(--hover-text, #fff);
}
.col-item-selected .ds-li-badge {
  background: rgba(255, 255, 255, 0.25);
  color: inherit;
}
.col-item-selected .ds-li-sub {
  color: rgba(255, 255, 255, 0.85);
}
</style>
